"use client"

import type React from "react"
import { useState, useRef, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FileUp, Download, FileText, Loader2, AlertCircle, Unlock, Eye, EyeOff } from "lucide-react"
import { toast } from "sonner"
import { Progress } from "@/components/ui/progress"
import { API_URL, API_KEY } from "@/lib/api-config"

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024 // 100MB

type ProcessingStatus = "idle" | "creating-job" | "uploading" | "processing" | "completed" | "error"

export function PdfUnlock() {
  const [file, setFile] = useState<File | null>(null)
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [outputFileName, setOutputFileName] = useState("unlocked")

  const [status, setStatus] = useState<ProcessingStatus>("idle")
  const [uploadProgress, setUploadProgress] = useState(0)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [resultInfo, setResultInfo] = useState<{ fileName: string; size: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    if (selectedFile.type !== "application/pdf" && !selectedFile.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please select a PDF file")
      return
    }

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size exceeds ${MAX_FILE_SIZE_BYTES / 1024 / 1024}MB limit`)
      return
    }

    setFile(selectedFile)
    setDownloadUrl(null)
    setResultInfo(null)
    setError(null)

    const baseName = selectedFile.name.replace(/\.pdf$/i, "")
    setOutputFileName(`${baseName}-unlocked`)

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }, [])

  const clearFile = useCallback(() => {
    setFile(null)
    setDownloadUrl(null)
    setResultInfo(null)
    setError(null)
    setPassword("")
    setOutputFileName("unlocked")
  }, [])

  const unlockPdf = async () => {
    if (!file) {
      toast.error("Please select a PDF file")
      return
    }

    if (!password) {
      toast.error("Please enter the PDF password")
      return
    }

    setStatus("creating-job")
    setError(null)
    setUploadProgress(0)

    try {
      // Step 1: Create job and get upload URL
      const createResponse = await fetch(`${API_URL}/api/v1/image/pdf/unlock`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_KEY,
        },
        body: JSON.stringify({
          fileName: file.name,
          fileSizeBytes: file.size,
          password,
          outputFileName,
        }),
      })

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to create unlock job")
      }

      const { jobId, uploadUrl } = await createResponse.json() as { jobId: string; uploadUrl: string }

      // Step 2: Upload PDF
      setStatus("uploading")

      const xhr = new XMLHttpRequest()
      await new Promise<void>((resolve, reject) => {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress((e.loaded / e.total) * 100)
          }
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error("Upload failed"))
          }
        }
        xhr.onerror = () => reject(new Error("Upload failed"))
        xhr.open("PUT", uploadUrl)
        xhr.setRequestHeader("Content-Type", "application/pdf")
        xhr.send(file)
      })

      // Step 3: Process PDF
      setStatus("processing")

      const processResponse = await fetch(`${API_URL}/api/v1/image/pdf/unlock/${jobId}/process`, {
        method: "POST",
        headers: {
          "X-API-Key": API_KEY,
        },
      })

      if (!processResponse.ok) {
        const errorData = await processResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to unlock PDF - check your password")
      }

      const result = await processResponse.json() as {
        downloadUrl: string
        fileName: string
        fileSizeBytes: number
      }

      setDownloadUrl(result.downloadUrl)
      setResultInfo({
        fileName: result.fileName,
        size: result.fileSizeBytes,
      })
      setStatus("completed")
      toast.success("PDF unlocked successfully!")
    } catch (err) {
      setStatus("error")
      const message = err instanceof Error ? err.message : "An error occurred"
      setError(message)
      toast.error(message)
    }
  }

  const downloadPdf = () => {
    if (!downloadUrl) return
    window.open(downloadUrl, "_blank")
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  const isProcessing = status === "creating-job" || status === "uploading" || status === "processing"

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Unlock className="h-5 w-5" />
          PDF Unlock
        </CardTitle>
        <CardDescription>
          Remove password protection from a PDF. You must know the current password.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* File Input */}
        <div className="space-y-4">
          <Input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleFileSelect}
            className="hidden"
            id="pdf-unlock-upload"
            disabled={isProcessing}
          />

          {!file ? (
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-32 border-dashed"
              disabled={isProcessing}
            >
              <div className="flex flex-col items-center gap-2">
                <FileUp className="h-8 w-8" />
                <span>Select Password-Protected PDF</span>
                <span className="text-xs text-muted-foreground">Max {MAX_FILE_SIZE_BYTES / 1024 / 1024}MB</span>
              </div>
            </Button>
          ) : (
            <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
              <FileText className="h-8 w-8 text-red-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{file.name}</p>
                <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={clearFile} disabled={isProcessing}>
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Password Input */}
        <div className="space-y-2">
          <Label htmlFor="password">PDF Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter the PDF password"
              disabled={isProcessing}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Enter either the user or owner password
          </p>
        </div>

        {/* Output filename */}
        <div className="space-y-2">
          <Label htmlFor="output-name">Output Filename</Label>
          <div className="flex items-center gap-2">
            <Input
              id="output-name"
              value={outputFileName}
              onChange={(e) => setOutputFileName(e.target.value)}
              placeholder="unlocked"
              disabled={isProcessing}
            />
            <span className="text-muted-foreground">.pdf</span>
          </div>
        </div>

        {/* Progress */}
        {isProcessing && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">
                {status === "creating-job" && "Creating unlock job..."}
                {status === "uploading" && `Uploading PDF... ${Math.round(uploadProgress)}%`}
                {status === "processing" && "Removing password protection..."}
              </span>
            </div>
            {status === "uploading" && <Progress value={uploadProgress} />}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Result */}
        {status === "completed" && resultInfo && (
          <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">PDF Unlocked Successfully!</p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {resultInfo.fileName} - {formatFileSize(resultInfo.size)}
                </p>
              </div>
              <Button onClick={downloadPdf}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          </div>
        )}

        {/* Unlock Button */}
        <Button
          onClick={unlockPdf}
          disabled={!file || !password || isProcessing}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Unlock className="h-4 w-4 mr-2" />
              Unlock PDF
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  )
}
